import { Spinner } from '@chakra-ui/react'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Menu } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { skipToken } from '@reduxjs/toolkit/query'
import moment from 'moment'
import type React from 'react'
import { useState } from 'react'
import InformationCircleIcon from '../../../../../assets/icons/information-circle-stroke-rounded'
import PageWrapper from '../../../../../components/Pagewrapper'
import Tooltip from '../../../../../components/Tooltip'
import {
  useGetCurrenciesQuery,
  useGetCustomerInsightsQuery,
} from '../../../../../redux/services/coreApi'
import type {
  CurrencyDto,
  CustomerInsightsFilterDto,
  Role,
  TradeType,
} from '../../../../../types/coreApi-types'
import { formatNumber } from '../../../../../utils/formatters'
import AverageOrderValueChart from './components/AverageOrderValueChart'
import CustomersByCountryTable from './components/CustomersByCountryTable'
import MetricCard from './components/MetricCard'
import OrdersByProductChart from './components/OrdersByProductChart'
import OrdersByTradeTypeChart from './components/OrdersByTradeTypeChart'
import OrdersPerDayChart from './components/OrdersPerDayChart'

import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'

type CustomerInsightsScreenProps = {}

// Metric descriptions for tooltips
const METRIC_DESCRIPTIONS = {
  ordersPerCustomer: `Total number of orders divided by total number of customers matching the filters.

Average number of orders per customer during the selected period. Includes all customers matching filters (role, country), including those with zero orders. Orders are counted only if created within the selected date range.`,

  orderVolumePerCustomer: `Sum of all order volumes (converted to EUR) divided by total number of customers matching the filters.

Average trading volume per customer. All volumes converted to EUR using exchange rates recorded at transaction time. Includes all customers matching filters, including those with zero volume.`,

  averageOrderSize: `Sum of all order volumes (converted to EUR) divided by total number of orders in the period.

Mean value of individual orders during the selected period. All volumes converted to EUR. Includes all orders matching applied filters (date, product, trade type).`,

  newCustomers: `Count of customers whose registration date falls within the selected date range, matching current filters (role, country).

Number of new customer registrations during the selected period. Includes only customers matching the role/country filters.`,

  activeCustomers: `Count of customers matching current filters who placed at least one order within the selected date range.

Number of customers active during the selected period. A customer is active if they placed at least one order in the period, regardless of registration date.`,

  passiveCustomers: `Count of customers matching current filters who placed zero orders within the selected date range.

Number of customers inactive during the selected period. Includes customers who never placed orders and customers who placed orders outside the period.`,

  privateCustomers: `Count of customers with role Individual matching current filters (country), regardless of registration date.

Total number of individual/private customer accounts in the system matching the filters. Not limited to customers who registered or were active in the selected period.`,

  businessCustomers: `Count of customers with role Organization matching current filters (country), regardless of registration date.

Total number of business/organization customer accounts in the system matching the filters. Not limited to customers who registered or were active in the selected period.`,
}

const CHART_DESCRIPTIONS = {
  ordersByProduct: `Orders grouped by the deposit currency (the currency the customer deposited).

Shows distribution of orders by product/currency during the selected period. Grouped by deposit currency (the product purchased in on-ramp or sold in off-ramp scenarios). Only orders created within the selected date range are included.`,

  ordersByTradeType: `Orders categorized as On-Ramp (fiat deposit → crypto withdrawal) or Off-Ramp (crypto deposit → fiat withdrawal).

Shows distribution between on-ramp (buying crypto with fiat) and off-ramp (selling crypto for fiat) orders. Classification based on currency types. Only orders created within the selected date range are included.`,

  ordersPerDay: `Time series of daily order counts and total volumes (in EUR) for each day within the selected date range.

Daily breakdown of order activity showing daily order count and total volume. Includes all days in the range, including days with zero orders. Data based on order creation date.`,

  averageOrderValueOverTime: `Time series of average order value (in EUR) per day, calculated as sum of order volumes divided by number of orders for that day.

Daily trend of average order values during the selected period. Shows mean value of orders placed each day. Days with zero orders show zero average. Data based on order creation date.`,

  customersByCountry: `Count of customers grouped by their country field, regardless of registration date or activity.

Geographic distribution of customers in the system. Shows which countries have the most customers. Includes all customers matching current filters (role), not just those active in the selected period.`,
}

const CustomerInsightsScreen: React.FC<CustomerInsightsScreenProps> = () => {
  const { data: currencies } = useGetCurrenciesQuery()

  const [filters, setFilters] = useState<CustomerInsightsFilterDto>({})

  const [date, setDate] = useState<[Date | null, Date | null]>([
    moment().subtract(30, 'days').toDate(),
    moment().toDate(),
  ])

  const [currentCurrency, setCurrentCurrency] = useState<CurrencyDto | null>(
    null
  )

  function dateToUtcDate(date: Date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  }

  const filterParams: CustomerInsightsFilterDto = {
    ...filters,
    fromDate: date[0] ? dateToUtcDate(date[0]).toISOString() : undefined,
    toDate: date[1]
      ? moment.utc(dateToUtcDate(date[1])).endOf('d').toISOString()
      : undefined,
    productId: currentCurrency?.id || undefined,
  }

  const { data: insights, isFetching: isLoading } = useGetCustomerInsightsQuery(
    date[0] && date[1] ? filterParams : skipToken,
    { skip: !date[0] || !date[1] }
  )

  const preSetDates = [
    {
      title: 'Last 7 days',
      fromDate: moment().subtract(7, 'days').toDate(),
      toDate: new Date(),
    },
    {
      title: 'Last 30 days',
      fromDate: moment().subtract(30, 'days').toDate(),
      toDate: new Date(),
    },
    {
      title: 'This month',
      fromDate: moment().startOf('month').toDate(),
      toDate: new Date(),
    },
    {
      title: 'Last month',
      fromDate: moment().subtract(1, 'month').startOf('month').toDate(),
      toDate: moment().subtract(1, 'month').endOf('month').toDate(),
    },
    {
      title: 'Last 3 months',
      fromDate: moment().subtract(3, 'months').toDate(),
      toDate: new Date(),
    },
    {
      title: 'Last 6 months',
      fromDate: moment().subtract(6, 'months').toDate(),
      toDate: new Date(),
    },
    {
      title: 'This year',
      fromDate: moment().startOf('year').toDate(),
      toDate: new Date(),
    },
  ]
  const [chosenPreDate, setChosenPreDate] = useState(preSetDates[1].title)

  const customerRoles: Role[] = ['Individual', 'Organization']
  const tradeTypes: TradeType[] = ['OnRamp', 'OffRamp']

  return (
    <PageWrapper title="Customer Insights">
      <div className="max-w-[1600px] mx-auto w-full">
        {/* Filters */}
        <div className="flex gap-2 pb-7 flex-wrap">
          <Menu shadow="md" position="bottom-start">
            <Menu.Target>
              <button className="bg-white hover:bg-brand-gray-100 gap-8 shadow-sm flex justify-between items-center h-full rounded-lg px-3 py-2.5 transition-all duration-300 text-sm font-medium">
                <p>{chosenPreDate}</p>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  color="brand-gray-800"
                />
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              {preSetDates?.map(preDates => (
                <Menu.Item
                  key={preDates.title}
                  component="a"
                  onClick={() => {
                    setChosenPreDate(preDates.title)
                    setDate([preDates.fromDate, preDates.toDate])
                  }}
                >
                  <div className="flex justify-between items-center w-[260px]">
                    <p className="flex-1">{preDates.title}</p>
                    <div className="flex flex-row gap-1 text-brand-dark-text-subtle-description text-xs">
                      <p>{moment(preDates.fromDate).format('DD MMM')}</p>-
                      <p className="flex-1">
                        {moment(preDates.toDate).format('DD MMM YYYY')}
                      </p>
                    </div>
                  </div>
                </Menu.Item>
              ))}
              <Menu.Item
                onClick={() => {
                  setChosenPreDate('Custom')
                }}
                component="a"
              >
                <p className="text-center">Custom</p>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {chosenPreDate === 'Custom' ? (
            <DatePickerInput
              popoverProps={{ defaultOpened: true }}
              type="range"
              placeholder={`${moment(date[0]).format('DD MMM YYYY')} - ${moment(date[1]).format('DD MMM YYYY')}`}
              value={date}
              valueFormat="D MMM YYYY"
              maxDate={new Date()}
              onChange={v => {
                if (!v[0] && !v[1]) {
                  setDate([v[0], new Date()])
                } else {
                  setDate(v)
                }
              }}
              classNames={{
                month: 'w-auto',
                input:
                  '!rounded-lg !border-none !shadow-sm hover:bg-brand-gray-100 !px-3 !py-2.5 !text-sm !font-medium !flex !justify-between !items-center',
              }}
            />
          ) : null}

          {/* Currency/Product Filter */}
          {currencies && currencies.length > 0 && (
            <Menu shadow="md" position="bottom-start">
              <Menu.Target>
                <button className="bg-white hover:bg-brand-gray-100 gap-8 shadow-sm flex justify-between items-center h-full rounded-lg px-3 py-2.5 transition-all duration-300 text-sm font-medium">
                  <p>
                    {currentCurrency
                      ? `${currentCurrency.name} (${currentCurrency.isoCode})`
                      : 'All Products'}
                  </p>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    size="xs"
                    color="brand-gray-800"
                  />
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  component="a"
                  onClick={() => {
                    setCurrentCurrency(null)
                    setFilters({ ...filters, productId: undefined })
                  }}
                >
                  All Products
                </Menu.Item>
                {currencies.map(currency => (
                  <Menu.Item
                    key={currency.id}
                    component="a"
                    onClick={() => {
                      setCurrentCurrency(currency)
                    }}
                  >
                    {currency.name} ({currency.isoCode})
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}

          {/* Customer Role Filter */}
          <Menu shadow="md" position="bottom-start">
            <Menu.Target>
              <button className="bg-white hover:bg-brand-gray-100 gap-8 shadow-sm flex justify-between items-center h-full rounded-lg px-3 py-2.5 transition-all duration-300 text-sm font-medium">
                <p>
                  {filters.customerRole
                    ? filters.customerRole === 'Individual'
                      ? 'Private'
                      : 'Business'
                    : 'All Roles'}
                </p>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  color="brand-gray-800"
                />
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                component="a"
                onClick={() => {
                  setFilters({ ...filters, customerRole: undefined })
                }}
              >
                All Roles
              </Menu.Item>
              {customerRoles.map(role => (
                <Menu.Item
                  key={role}
                  component="a"
                  onClick={() => {
                    setFilters({ ...filters, customerRole: role })
                  }}
                >
                  {role === 'Individual' ? 'Private' : 'Business'}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          {/* Trade Type Filter */}
          <Menu shadow="md" position="bottom-start">
            <Menu.Target>
              <button className="bg-white hover:bg-brand-gray-100 gap-8 shadow-sm flex justify-between items-center h-full rounded-lg px-3 py-2.5 transition-all duration-300 text-sm font-medium">
                <p>
                  {filters.tradeType
                    ? filters.tradeType === 'OnRamp'
                      ? 'On-Ramp'
                      : 'Off-Ramp'
                    : 'All Trade Types'}
                </p>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  color="brand-gray-800"
                />
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                component="a"
                onClick={() => {
                  setFilters({ ...filters, tradeType: undefined })
                }}
              >
                All Trade Types
              </Menu.Item>
              {tradeTypes.map(tradeType => (
                <Menu.Item
                  key={tradeType}
                  component="a"
                  onClick={() => {
                    setFilters({ ...filters, tradeType })
                  }}
                >
                  {tradeType === 'OnRamp' ? 'On-Ramp' : 'Off-Ramp'}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          {/* Country Filter */}
          <input
            type="text"
            placeholder="Country (e.g., DNK)"
            className="bg-white hover:bg-brand-gray-100 shadow-sm rounded-lg px-3 py-2.5 text-sm font-medium border-none focus:outline-none focus:ring-2 focus:ring-brand-gray-200"
            value={filters.country || ''}
            onChange={e => {
              setFilters({
                ...filters,
                country: e.target.value || undefined,
              })
            }}
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center w-full py-8">
            <Spinner />
          </div>
        )}

        {/* Data Display */}
        {!isLoading && insights && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <MetricCard
                title="Orders per Customer"
                value={insights.ordersPerCustomer.currentValue.toFixed(2)}
                previousValue={insights.ordersPerCustomer.previousValue}
                percentageChange={insights.ordersPerCustomer.percentageChange}
                description={METRIC_DESCRIPTIONS.ordersPerCustomer}
              />
              <MetricCard
                title="Order Volume per Customer"
                value={formatNumber(
                  insights.orderVolumePerCustomer.currentValue,
                  {
                    currencyType: 'FiatCurrency',
                    isoCode: 'EUR',
                  },
                  2
                )}
                previousValue={insights.orderVolumePerCustomer.previousValue}
                percentageChange={
                  insights.orderVolumePerCustomer.percentageChange
                }
                description={METRIC_DESCRIPTIONS.orderVolumePerCustomer}
              />
              <MetricCard
                title="Average Order Size"
                value={formatNumber(insights.averageOrderSize.currentValue, {
                  currencyType: 'FiatCurrency',
                  isoCode: 'EUR',
                })}
                previousValue={insights.averageOrderSize.previousValue}
                percentageChange={insights.averageOrderSize.percentageChange}
                description={METRIC_DESCRIPTIONS.averageOrderSize}
              />
              <MetricCard
                title="New Customers"
                value={insights.newCustomers.currentValue.toString()}
                previousValue={insights.newCustomers.previousValue}
                percentageChange={insights.newCustomers.percentageChange}
                description={METRIC_DESCRIPTIONS.newCustomers}
              />
            </div>

            {/* Customer Counts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <MetricCard
                title="Active Customers"
                value={insights.activeCustomers.currentValue.toString()}
                previousValue={insights.activeCustomers.previousValue}
                percentageChange={insights.activeCustomers.percentageChange}
                description={METRIC_DESCRIPTIONS.activeCustomers}
              />
              <MetricCard
                title="Passive Customers"
                value={insights.passiveCustomers.currentValue.toString()}
                previousValue={insights.passiveCustomers.previousValue}
                percentageChange={insights.passiveCustomers.percentageChange}
                description={METRIC_DESCRIPTIONS.passiveCustomers}
              />
              <MetricCard
                title="Private Customers"
                value={insights.privateCustomers.currentValue.toString()}
                previousValue={insights.privateCustomers.previousValue}
                percentageChange={insights.privateCustomers.percentageChange}
                description={METRIC_DESCRIPTIONS.privateCustomers}
              />
              <MetricCard
                title="Business Customers"
                value={insights.businessCustomers.currentValue.toString()}
                previousValue={insights.businessCustomers.previousValue}
                percentageChange={insights.businessCustomers.percentageChange}
                description={METRIC_DESCRIPTIONS.businessCustomers}
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white shadow-sm p-6 rounded-2xl border-solid border-[1px] border-black/10">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold">Orders by Product</h2>
                  <Tooltip
                    label={
                      <div className="whitespace-pre-line text-sm">
                        {CHART_DESCRIPTIONS.ordersByProduct}
                      </div>
                    }
                    placement="top"
                    maxWidth={400}
                  >
                    <InformationCircleIcon
                      className="w-4 h-4 text-gray-400 cursor-help"
                      hideTitle={true}
                    />
                  </Tooltip>
                </div>
                <OrdersByProductChart data={insights.ordersByProduct} />
              </div>

              <div className="bg-white shadow-sm p-6 rounded-2xl border-solid border-[1px] border-black/10">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold">
                    Orders by Trade Type
                  </h2>
                  <Tooltip
                    label={
                      <div className="whitespace-pre-line text-sm">
                        {CHART_DESCRIPTIONS.ordersByTradeType}
                      </div>
                    }
                    placement="top"
                    maxWidth={400}
                  >
                    <InformationCircleIcon
                      className="w-4 h-4 text-gray-400 cursor-help"
                      hideTitle={true}
                    />
                  </Tooltip>
                </div>
                <OrdersByTradeTypeChart data={insights.ordersByTradeType} />
              </div>
            </div>

            {/* Time Series Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white shadow-sm p-6 rounded-2xl border-solid border-[1px] border-black/10">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold">Orders per Day</h2>
                  <Tooltip
                    label={
                      <div className="whitespace-pre-line text-sm">
                        {CHART_DESCRIPTIONS.ordersPerDay}
                      </div>
                    }
                    placement="top"
                    maxWidth={400}
                  >
                    <InformationCircleIcon
                      className="w-4 h-4 text-gray-400 cursor-help"
                      hideTitle={true}
                    />
                  </Tooltip>
                </div>
                <OrdersPerDayChart data={insights.ordersPerDay} />
              </div>

              <div className="bg-white shadow-sm p-6 rounded-2xl border-solid border-[1px] border-black/10">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold">
                    Average Order Value Over Time
                  </h2>
                  <Tooltip
                    label={
                      <div className="whitespace-pre-line text-sm">
                        {CHART_DESCRIPTIONS.averageOrderValueOverTime}
                      </div>
                    }
                    placement="top"
                    maxWidth={400}
                  >
                    <InformationCircleIcon
                      className="w-4 h-4 text-gray-400 cursor-help"
                      hideTitle={true}
                    />
                  </Tooltip>
                </div>
                <AverageOrderValueChart
                  data={insights.averageOrderValueOverTime}
                />
              </div>
            </div>

            {/* Customers by Country */}
            <div className="bg-white shadow-sm p-6 rounded-2xl border-solid border-[1px] border-black/10">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-lg font-semibold">Customers by Country</h2>
                <Tooltip
                  label={
                    <div className="whitespace-pre-line text-sm">
                      {CHART_DESCRIPTIONS.customersByCountry}
                    </div>
                  }
                  placement="top"
                  maxWidth={400}
                >
                  <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <CustomersByCountryTable data={insights.customersByCountry} />
            </div>
          </>
        )}
      </div>
    </PageWrapper>
  )
}

export default CustomerInsightsScreen
